/** Whole seconds from HTMLMediaElement.duration (NaN/Infinity → null). */
export const durationSecondsFromMedia = (duration) => {
  if (!Number.isFinite(duration) || duration <= 0) return null;
  return Math.max(1, Math.round(duration));
};

export const parseDurationSeconds = (raw) => {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
};

/**
 * Read track length from an audio URL (or object URL) via metadata only.
 * Resolves null on missing src, error, timeout, or unknown duration.
 */
export const readAudioDurationSeconds = (
  src,
  { createAudio, timeoutMs = 8000 } = {}
) =>
  new Promise((resolve) => {
    const trimmed = String(src || '').trim();
    const AudioCtor =
      createAudio || (typeof Audio !== 'undefined' ? Audio : null);
    if (!trimmed || !AudioCtor) {
      resolve(null);
      return;
    }

    let settled = false;
    const audio = new AudioCtor();
    const finish = (value) => {
      if (settled) return;
      settled = true;
      audio.onloadedmetadata = null;
      audio.onerror = null;
      try {
        audio.src = '';
      } catch {
        /* ignore */
      }
      resolve(value);
    };

    const timer = setTimeout(() => finish(null), timeoutMs);
    const done = (value) => {
      clearTimeout(timer);
      finish(value);
    };

    audio.preload = 'metadata';
    audio.onloadedmetadata = () => done(durationSecondsFromMedia(audio.duration));
    audio.onerror = () => done(null);
    try {
      audio.src = trimmed;
    } catch {
      done(null);
    }
  });

export const readAudioFileDurationSeconds = async (file, options) => {
  if (!file || typeof URL === 'undefined' || !URL.createObjectURL) return null;
  const objectUrl = URL.createObjectURL(file);
  try {
    return await readAudioDurationSeconds(objectUrl, options);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Attach detected duration to create/update payload.
 * - create: include only when detected
 * - update: send when newly detected / changed; omit when unknown (preserve)
 */
export const applyAudioDurationToPayload = (
  payload,
  { current, original, isCreate }
) => {
  const next = parseDurationSeconds(current);
  if (isCreate) {
    if (next != null) payload.audio_duration_seconds = next;
    return payload;
  }
  const prev = parseDurationSeconds(original);
  if (next == null || next === prev) return payload;
  payload.audio_duration_seconds = next;
  return payload;
};
